import React, { useState, useEffect } from 'react';
import api from '../api';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend, isSameDay, parseISO, addMonths, subMonths, isWithinInterval } from 'date-fns';

const TeamCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const typeColours = {
    holiday: { bg: 'rgba(57,60,91,0.12)', text: 'var(--navy)', label: 'Holiday' },
    sick: { bg: 'rgba(184,134,11,0.12)', text: 'var(--warning)', label: 'Sick' },
    awol: { bg: 'rgba(155,58,58,0.12)', text: 'var(--danger)', label: 'AWOL' },
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const from = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
        const to = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
        const res = await api.get(`/api/holidays/calendar?from=${from}&to=${to}`);
        setEvents(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentMonth]);

  const days = eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) });
  const startDow = startOfMonth(currentMonth).getDay(); // 0 = Sun
  const adjustedStart = startDow === 0 ? 6 : startDow - 1; // Mon-based

  const getEventsForDay = (day) => {
    return events.filter(e => {
      const start = new Date(e.start_date + "T00:00:00");
      const end = new Date(e.end_date + "T00:00:00");
      return isWithinInterval(day, { start, end });
    });
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Team Calendar</h1>
          <p className="page-subtitle">Approved absences across the team</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button className="btn btn-outline btn-sm" onClick={() => setCurrentMonth(m => subMonths(m, 1))}>&#8592;</button>
          <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '20px', minWidth: '160px', textAlign: 'center' }}>
            {format(currentMonth, 'MMMM yyyy')}
          </span>
          <button className="btn btn-outline btn-sm" onClick={() => setCurrentMonth(m => addMonths(m, 1))}>&#8594;</button>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
        {Object.entries(typeColours).map(([key, val]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: val.bg, border: `1px solid ${val.text}` }} />
            <span style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>{val.label}</span>
          </div>
        ))}
      </div>

      <div className="card">
        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--border)' }}>
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
            <div key={d} style={{ padding: '10px', textAlign: 'center', fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--grey-mid)', fontWeight: '500' }}>{d}</div>
          ))}
        </div>

        {loading ? (
          <div className="loader">Loading...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {/* Empty cells before month start */}
            {Array.from({ length: adjustedStart }).map((_, i) => (
              <div key={`empty-${i}`} style={{ minHeight: '100px', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', background: 'var(--grey-light)' }} />
            ))}

            {days.map((day, idx) => {
              const isWeekendDay = isWeekend(day);
              const dayEvents = getEventsForDay(day);
              const isToday = isSameDay(day, new Date());

              return (
                <div key={idx} style={{
                  minHeight: '100px',
                  borderRight: '1px solid var(--border)',
                  borderBottom: '1px solid var(--border)',
                  padding: '8px',
                  background: isWeekendDay ? 'var(--grey-light)' : 'var(--white)',
                  position: 'relative'
                }}>
                  <div style={{
                    width: '26px', height: '26px', borderRadius: '50%',
                    background: isToday ? 'var(--navy)' : 'transparent',
                    color: isToday ? 'var(--gold)' : isWeekendDay ? 'var(--grey-mid)' : 'var(--navy)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: isToday ? '600' : '400',
                    marginBottom: '4px'
                  }}>
                    {format(day, 'd')}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {dayEvents.map((e, i) => {
                      const colours = typeColours[e.absence_type] || typeColours.holiday;
                      return (
                        <div key={i} style={{
                          background: colours.bg,
                          color: colours.text,
                          borderRadius: '3px',
                          padding: '2px 6px',
                          fontSize: '10px',
                          fontWeight: '500',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }} title={`${e.staff_name} - ${colours.label}`}>
                          {e.staff_name.split(' ')[0]}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamCalendar;
